import { useEffect, useMemo, useState } from 'react';
import type { RepositoryAnalysis } from '../types/analysis.types';
import { OverviewSection } from './OverviewSection';
import { RecommendationsSection } from './RecommendationsSection';

interface AnalysisResultsViewProps {
  data: RepositoryAnalysis;
  onBackToRepository?: () => void;
}

type ViewType = 'overview' | 'recommendations';

export const AnalysisResultsView: React.FC<AnalysisResultsViewProps> = ({
  data,
  onBackToRepository,
}) => {
  const [currentView, setCurrentView] = useState<ViewType>('overview');
  const projects = useMemo(() => projectsWithAggregate(data), [data]);
  const [selectedProject, setSelectedProject] = useState<RepositoryAnalysis | null>(
    projects[0] ?? null,
  );

  const navigateToRecommendations = () => {
    setCurrentView('recommendations');
  };

  const navigateToOverview = () => {
    setCurrentView('overview');
  };

  // Get grounding score from validation metadata
  const groundingScore = data._validation?.groundingScore;
  const activeReport = projects.length > 0 ? (selectedProject ?? projects[0] ?? data) : data;

  useEffect(() => {
    setSelectedProject((current) => {
      if (projects.length === 0) {
        return null;
      }
      if (current && projects.some((project) => sameProject(project, current))) {
        return current;
      }
      return projects[0];
    });
  }, [projects]);

  return (
    <div className="min-h-screen transition-colors duration-200 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {currentView === 'overview' && (
        <OverviewSection
          key={`overview-${data.score}-${data.categories?.length || 0}`}
          data={data}
          onNavigateToRecommendations={navigateToRecommendations}
          onBackToRepository={onBackToRepository}
          groundingScore={groundingScore}
          projects={projects}
          selectedProject={selectedProject}
          onSelectProject={setSelectedProject}
        />
      )}

      {currentView === 'recommendations' && (
        <RecommendationsSection
          key={`recommendations-${activeReport.repositoryName}-${activeReport.recommendations?.length || 0}`}
          data={activeReport}
          onNavigateToOverview={navigateToOverview}
        />
      )}
    </div>
  );
};

function sameProject(a: RepositoryAnalysis, b: RepositoryAnalysis): boolean {
  if (a.projectIndex !== undefined && b.projectIndex !== undefined) {
    return a.projectIndex === b.projectIndex;
  }
  if (a.projectPath && b.projectPath) {
    return a.projectPath === b.projectPath;
  }
  return a.repositoryName === b.repositoryName;
}

function projectsWithAggregate(data: RepositoryAnalysis): RepositoryAnalysis[] {
  const projects = data.projects ?? [];
  if (data.projectIndex !== -1) {
    return projects;
  }
  if (projects.some((project) => project.projectIndex === -1)) {
    return projects;
  }
  return [data, ...projects];
}
